import type { DocPageContent } from '../docs-content'

const page = {
  order: 18,
  slug: 'export',
  title: 'Exportación',
  description:
    'Renderiza vídeo o audio, elige formatos y rango, y resuelve advertencias de pre-vuelo.',
  category: 'Salida',
  related: ['render-queue', 'text-captions-subtitles', 'troubleshooting'],
  sections: [
    {
      title: 'Configurar una exportación',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Abre **Exportar** (`Ctrl+Mayús+E`) y elige **Exportar Vídeo** o **Exportar Audio**.',
            'Renderiza todo el proyecto, o el rango de entrada/salida actual cuando solo necesites una sección.',
            'Establece **Resolución** y **Calidad** según el objetivo: una revisión rápida, un archivo para compartir o un máster final.',
            'Resuelve cualquier advertencia de pre-vuelo y luego renderiza.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Una mayor resolución y calidad tardan más y producen archivos más grandes.',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Cuando un proyecto tiene más de la línea de tiempo Principal, un menú **Secuencia** en la parte superior del diálogo permite elegir qué línea de tiempo exportar — con su propia resolución y rango.',
        },
      ],
    },
    {
      title: 'Formatos',
      blocks: [
        {
          kind: 'table',
          headers: ['Salida', 'Formatos'],
          rows: [
            ['Contenedores de vídeo', 'MP4, MOV, WebM, MKV'],
            ['Solo audio', 'MP3, AAC, WAV'],
            ['Pista de subtítulos suaves', 'WebM, MKV'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Los códecs disponibles dependen de tu sistema; CachoCut advierte o recurre a una alternativa cuando uno no está disponible.',
        },
      ],
    },
    {
      title: 'Subtítulos',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Cuando una exportación de vídeo tiene una transcripción, el control **Subtítulos** elige cómo se incluyen los subtítulos. Las opciones se adaptan al contenedor que hayas elegido.',
        },
        {
          kind: 'table',
          headers: ['Modo', 'Qué hace'],
          rows: [
            ['**Desactivado**', 'Sin subtítulos en el vídeo exportado.'],
            ['**Grabados**', 'Los subtítulos se dibujan en el fotograma, por lo que se muestran en todos los reproductores.'],
            [
              '**Archivo externo** `.srt`',
              'Vídeo limpio más un archivo de subtítulos `.srt` independiente que se descarga junto a él.',
            ],
            [
              '**Pista incrustada**',
              'Una pista de subtítulos suave y conmutable que se transporta dentro del contenedor.',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'La opción de pista incrustada solo aparece para `WebM` y `MKV`. En `MP4` y `MOV`, elige grabados o un archivo externo, o cambia de contenedor para una pista conmutable.',
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Algunos reproductores ocultan las pistas de subtítulos por defecto, por lo que los espectadores pueden tener que activarlas.',
        },
      ],
    },
    {
      title: 'Comprobaciones de pre-vuelo',
      blocks: [
        {
          kind: 'paragraph',
          text: 'El pre-vuelo se ejecuta antes del renderizado y señala bloqueadores comunes: un rango vacío, medios faltantes, un códec no disponible, una caída a exportación por worker, un archivo grande o un renderizado largo.',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Para una comprobación rápida, exporta primero un rango de entrada/salida corto antes de comprometerte con el renderizado completo.',
        },
      ],
    },
    {
      title: 'Renderizar y guardar',
      blocks: [
        {
          kind: 'list',
          items: [
            'El progreso avanza a través de preparación, renderizado, codificación y finalización, y puedes **Cancelar** en cualquier momento.',
            'La exportación por worker se ejecuta en segundo plano cuando está disponible; una alternativa en el hilo principal requiere que la pestaña esté enfocada y en reposo.',
            'Cuando se completa, elige **Guardar** para guardar el archivo, que también se escribe en la carpeta de exportaciones de tu espacio de trabajo.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'Mantén abierta la pestaña del proyecto hasta que finalice la exportación. Para renderizar varios archivos por lotes, añádelos a la **Cola de renderizado** en lugar de exportar uno por uno.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page